import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { notificationDatabaseService } from '../../services/notificationDatabaseService';
import AppIcon from '../../components/AppIcon';
import { cn } from '../../utils/cn';
import Button from '../../components/ui/Button';

const Notifications = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNotifications();
  }, []);

  useEffect(() => {
    if (!user?.id) return;

    const channel = notificationDatabaseService.subscribeToNotifications(user.id, (newNotification) => {
      setNotifications((prev) => [newNotification, ...prev]);
    });

    return () => {
      notificationDatabaseService.unsubscribeFromNotifications(channel);
    };
  }, [user?.id]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationDatabaseService.getMyNotifications();
      setNotifications(data || []);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationDatabaseService.markAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
      );
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationDatabaseService.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    const iconMap = {
      booking: 'Calendar',
      system: 'Bell',
      payment: 'CreditCard',
      review: 'Star',
    };
    return iconMap[type] || 'Bell';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'الآن';
    if (minutes < 60) return `منذ ${minutes} دقيقة`;
    if (hours < 24) return `منذ ${hours} ساعة`;
    if (days < 7) return `منذ ${days} يوم`;
    return date.toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
              الإشعارات
            </h1>
            {unreadCount > 0 && (
              <p className="text-sm text-slate-600">
                لديك {unreadCount} إشعار غير مقروء
              </p>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              onClick={handleMarkAllAsRead}
              variant="outline"
              size="sm"
              iconName="Check"
            >
              تحديد الكل كمقروء
            </Button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-3">
        {notifications.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
            <AppIcon name="BellOff" size={40} color="#94a3b8" className="mx-auto mb-4 opacity-50" />
            <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
              لا توجد إشعارات
            </p>
          </div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.id}
              className={cn(
                'bg-white rounded-2xl shadow-sm border p-4 transition-colors text-sm',
                !notification.isRead
                  ? 'border-primary bg-primary/5'
                  : 'border-slate-200'
              )}
            >
              <div className="flex items-start gap-3" dir="rtl">
                <div
                  className={cn(
                    'p-2 rounded-lg flex-shrink-0',
                    !notification.isRead ? 'bg-primary/10' : 'bg-slate-100'
                  )}
                >
                  <AppIcon
                    name={getNotificationIcon(notification.type)}
                    size={18}
                    color={!notification.isRead ? 'var(--color-primary)' : '#64748b'}
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3
                      className={cn(
                        'text-sm font-semibold leading-snug',
                        !notification.isRead ? 'text-slate-900' : 'text-slate-700'
                      )}
                      style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
                    >
                      {notification.title || 'إشعار جديد'}
                    </h3>
                    {!notification.isRead && (
                      <button
                        onClick={() => handleMarkAsRead(notification.id)}
                        className="p-1 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
                        title="تحديد كمقروء"
                      >
                        <AppIcon name="Circle" size={14} color="var(--color-primary)" />
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mb-2 leading-relaxed">
                    {notification.message || notification.body || ''}
                  </p>
                  <p className="text-xs text-slate-400">
                    {formatDate(notification.createdAt || notification.created_at)}
                  </p>
                  {notification.type === 'booking' && (
                    <Button
                      onClick={() => navigate('/photographer-dashboard/bookings?tab=pending')}
                      variant="outline"
                      size="sm"
                      className="mt-3 w-full text-xs"
                    >
                      عرض الطلب 
                    </Button>
                  )}
                  {notification.type === 'support' && (
                    <button
                      onClick={() => navigate('/photographer-dashboard/support')}
                      className="mt-3 w-full py-2 px-4 border border-amber-300 text-amber-700 bg-amber-50 rounded-xl text-xs font-medium hover:bg-amber-100 transition-colors flex items-center justify-center gap-2"
                    >
                      <AppIcon name="LifeBuoy" size={13} />
                      عرض التذكرة
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Notifications;
